/**
 * Study Mode Testimonials component - Carousel style testimonials
 */

import {useState, useEffect} from 'react';
import {Container} from '../ui/Container';
import {Section} from '../ui/Section';
import {useScrollAnimation} from '../../hooks/useScrollAnimation';

interface Testimonial {
  quote: string;
  author: string;
  role: string;
  company?: string;
  logo?: string;
}

const testimonials: Testimonial[] = [
  {
    quote: "Study Mode transformed how I prepare for exams. I upload my lecture notes, and it instantly creates flashcards and practice questions. The AI chat helps me understand concepts I'm struggling with. It's like having a personal tutor available 24/7.",
    author: "Sarah Chen",
    role: "Biology Student",
    company: "Stanford University",
  },
  {
    quote: "As a self-learner studying data science, Study Mode helps me consolidate information from multiple courses. The knowledge graph feature shows me how concepts connect, which is incredibly valuable for understanding complex topics.",
    author: "Marcus Rodriguez",
    role: "Data Science Student",
  },
  {
    quote: "The voice assistant mode is a game-changer. I can study while commuting or walking to class. It's like having a conversation with a tutor who remembers everything we've discussed.",
    author: "Alex Kim",
    role: "High School Senior",
    company: "AP Exam Prep",
  },
  {
    quote: "Study Mode's ability to generate markschemes when I don't have them saved me hours of work. The feedback is detailed and actually helps me understand where I went wrong, not just what the right answer is.",
    author: "Emily Johnson",
    role: "Mathematics Student",
    company: "Cambridge University",
  },
  {
    quote: "I love how Study Mode organizes my messy handwritten notes into structured documents. The AI understands context and creates proper headings, bullet points, and highlights key information automatically.",
    author: "David Park",
    role: "Engineering Student",
  },
  {
    quote: "The spaced repetition feature keeps me on track with my review schedule. I don't have to think about when to review - Study Mode tells me exactly what I need to practice based on my performance.",
    author: "Jessica Martinez",
    role: "Medical Student",
    company: "Johns Hopkins",
  },
];

export function StudyTestimonials() {
  const {ref, isVisible} = useScrollAnimation();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  // Auto-advance carousel
  useEffect(() => {
    if (!isAutoPlaying) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [isAutoPlaying, testimonials.length]);

  const visibleTestimonials = testimonials.slice(currentIndex, currentIndex + 4);
  if (visibleTestimonials.length < 4) {
    visibleTestimonials.push(...testimonials.slice(0, 4 - visibleTestimonials.length));
  }

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
    setIsAutoPlaying(false);
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    setIsAutoPlaying(false);
  };

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
    setIsAutoPlaying(false);
  };

  return (
    <Section padding="lg" background="gray" ref={ref} className="bg-backgroundSecondary dark:bg-[#252526]">
      <Container>
        <div className={`transition-all duration-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          {/* Header */}
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-textPrimary dark:text-[#CCCCCC] mb-4">
              What Students Say
            </h2>
            <p className="text-lg text-textSecondary dark:text-[#858585] max-w-2xl mx-auto">
              See how Study Mode is helping students worldwide master their subjects
            </p>
          </div>

          {/* Testimonials Carousel */}
          <div className="relative max-w-7xl mx-auto">
            {/* Testimonials Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {visibleTestimonials.map((testimonial, index) => (
                <div
                  key={`${currentIndex}-${index}`}
                  className="bg-background dark:bg-[#1E1E1E] rounded-lg p-6 border border-gray-200 dark:border-gray-700 h-full flex flex-col"
                >
                  {/* Quote */}
                  <p className="text-sm text-textSecondary dark:text-[#858585] leading-relaxed mb-6 flex-1">
                    "{testimonial.quote}"
                  </p>
                  
                  {/* Author */}
                  <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                    <p className="font-semibold text-textPrimary dark:text-[#CCCCCC] text-sm">
                      {testimonial.author}
                    </p>
                    <p className="text-xs text-textTertiary dark:text-[#6A6A6A] mt-1">
                      {testimonial.role}
                      {testimonial.company && `, ${testimonial.company}`}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Navigation Controls */}
            <div className="flex items-center justify-center gap-4">
              <button
                onClick={goToPrevious}
                className="w-10 h-10 rounded-full border border-gray-300 dark:border-gray-600 bg-background dark:bg-[#1E1E1E] flex items-center justify-center hover:bg-backgroundSecondary dark:hover:bg-[#252526] hover:border-orange-500/50 transition-colors"
                aria-label="Previous testimonial"
              >
                <svg className="w-5 h-5 text-textPrimary dark:text-[#CCCCCC] group-hover:text-orange-600/70 dark:group-hover:text-orange-500/70 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>

              {/* Pagination Dots */}
              <div className="flex items-center gap-2">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => goToSlide(index)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      index === currentIndex
                        ? 'bg-orange-600 dark:bg-orange-500 w-8'
                        : 'bg-gray-400 dark:bg-gray-600 hover:bg-orange-500/50 dark:hover:bg-orange-500/50'
                    }`}
                    aria-label={`Go to testimonial ${index + 1}`}
                  />
                ))}
              </div>

              <button
                onClick={goToNext}
                className="w-10 h-10 rounded-full border border-gray-300 dark:border-gray-600 bg-background dark:bg-[#1E1E1E] flex items-center justify-center hover:bg-backgroundSecondary dark:hover:bg-[#252526] hover:border-orange-500/50 transition-colors"
                aria-label="Next testimonial"
              >
                <svg className="w-5 h-5 text-textPrimary dark:text-[#CCCCCC] group-hover:text-orange-600/70 dark:group-hover:text-orange-500/70 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </Container>
    </Section>
  );
}

